import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '../ui/collapsible';
import { ChevronDown, ChevronRight, Save, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';
import PermissionSection from './PermissionSection';
import type {
  ProjectPermissions,
  RolePermissions,
  ResourcePermission,
  ResourceName
} from '../../types/permissions';
import {
  getProjectPermissions,
  updateProjectPermissions,
  getRoleName
} from '../../services/permissionsApi';
import { usePermissions } from '../../contexts/PermissionsContext';

/**
 * PermissionsTab Component
 * Main UI for managing project permissions
 * Only accessible to project owners and admins
 */
export const PermissionsTab: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { isOwnerOrAdmin } = usePermissions();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [originalPermissions, setOriginalPermissions] = useState<ProjectPermissions | null>(null);
  const [memberPermissions, setMemberPermissions] = useState<RolePermissions | null>(null);
  const [viewerPermissions, setViewerPermissions] = useState<RolePermissions | null>(null);

  const [memberOpen, setMemberOpen] = useState(true);
  const [viewerOpen, setViewerOpen] = useState(false);

  const resources: ResourceName[] = ['projects', 'tasks', 'serviceOrders', 'hoursBank', 'team', 'settings'];

  /**
   * Load permissions from API
   */
  const loadPermissions = async () => {
    if (!projectId) return;

    try {
      setIsLoading(true);
      setError(null);

      const data = await getProjectPermissions(projectId);
      setOriginalPermissions(data);
      setMemberPermissions(data.member);
      setViewerPermissions(data.viewer);
    } catch (err: any) {
      console.error('Error loading permissions:', err);
      setError(err.response?.data?.message || 'Erro ao carregar permissões');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Save permissions changes
   */
  const savePermissions = async () => {
    if (!projectId || !memberPermissions || !viewerPermissions) return;

    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      await updateProjectPermissions(projectId, {
        member: memberPermissions,
        viewer: viewerPermissions
      });

      setSuccess('Permissões atualizadas com sucesso!');

      // Reload to get updated data
      await loadPermissions();
    } catch (err: any) {
      console.error('Error saving permissions:', err);
      setError(err.response?.data?.message || 'Erro ao salvar permissões');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle permission change for a resource
   */
  const handleMemberChange = (resource: ResourceName, permissions: ResourcePermission) => {
    if (!memberPermissions) return;

    setMemberPermissions({
      ...memberPermissions,
      [resource]: permissions
    });
  };

  const handleViewerChange = (resource: ResourceName, permissions: ResourcePermission) => {
    if (!viewerPermissions) return;

    setViewerPermissions({
      ...viewerPermissions,
      [resource]: permissions
    });
  };

  /**
   * Reset to original permissions
   */
  const resetPermissions = () => {
    if (originalPermissions) {
      setMemberPermissions(originalPermissions.member);
      setViewerPermissions(originalPermissions.viewer);
      setSuccess(null);
      setError(null);
    }
  };

  /**
   * Check if there are unsaved changes
   */
  const hasChanges = (): boolean => {
    if (!originalPermissions || !memberPermissions || !viewerPermissions) return false;

    return (
      JSON.stringify(memberPermissions) !== JSON.stringify(originalPermissions.member) ||
      JSON.stringify(viewerPermissions) !== JSON.stringify(originalPermissions.viewer)
    );
  };

  /**
   * Load permissions on mount
   */
  useEffect(() => {
    loadPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Warn before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges()) {
        e.preventDefault();
        e.returnValue = 'Você tem alterações não salvas. Deseja sair mesmo assim?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [memberPermissions, viewerPermissions, originalPermissions]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-gray-400">Carregando permissões...</span>
      </div>
    );
  }

  if (!isOwnerOrAdmin()) {
    return (
      <Alert className="bg-red-900/20 border-red-800">
        <AlertCircle className="h-5 w-5 text-red-500" />
        <AlertDescription className="text-red-300">
          Apenas proprietários e administradores podem gerenciar permissões.
        </AlertDescription>
      </Alert>
    );
  }

  if (!memberPermissions || !viewerPermissions) {
    return (
      <Alert className="bg-red-900/20 border-red-800">
        <AlertCircle className="h-5 w-5 text-red-500" />
        <AlertDescription className="text-red-300">
          Erro ao carregar permissões. Por favor, tente novamente.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Gerenciar Permissões</h2>
          <p className="text-sm text-gray-400 mt-1">
            Configure as permissões para membros e visualizadores do projeto
          </p>
        </div>

        {hasChanges() && (
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={resetPermissions}
              disabled={isSaving}
              className="border-gray-600 hover:bg-gray-800"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Resetar
            </Button>
            <Button
              onClick={savePermissions}
              disabled={isSaving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </div>
        )}
      </div>

      {/* Success/Error Messages */}
      {success && (
        <Alert className="bg-green-900/20 border-green-800">
          <CheckCircle2 className="h-5 w-5 text-green-500" />
          <AlertDescription className="text-green-300">{success}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert className="bg-red-900/20 border-red-800">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <AlertDescription className="text-red-300">{error}</AlertDescription>
        </Alert>
      )}

      {/* Info Box */}
      <Alert className="bg-blue-900/20 border-blue-800">
        <AlertCircle className="h-5 w-5 text-blue-400" />
        <AlertDescription className="text-blue-300">
          <strong>Importante:</strong> As permissões de Proprietário e Administrador são fixas e não podem ser alteradas.
          Apenas as permissões de Membro e Visualizador são customizáveis.
        </AlertDescription>
      </Alert>

      {/* Member Permissions */}
      <Collapsible open={memberOpen} onOpenChange={setMemberOpen}>
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between p-4 border border-gray-700 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors">
            <div className="flex items-center space-x-3">
              {memberOpen ? (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
              <h3 className="text-lg font-semibold text-white">
                {getRoleName('member')}
              </h3>
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="mt-4 space-y-3">
            {resources.map((resource) => (
              <PermissionSection
                key={resource}
                resource={resource}
                permissions={memberPermissions[resource]}
                onChange={handleMemberChange}
                disabled={isSaving}
              />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Viewer Permissions */}
      <Collapsible open={viewerOpen} onOpenChange={setViewerOpen}>
        <CollapsibleTrigger className="w-full">
          <div className="flex items-center justify-between p-4 border border-gray-700 rounded-lg bg-gray-800/50 hover:bg-gray-800 transition-colors">
            <div className="flex items-center space-x-3">
              {viewerOpen ? (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronRight className="w-5 h-5 text-gray-400" />
              )}
              <h3 className="text-lg font-semibold text-white">
                {getRoleName('viewer')}
              </h3>
            </div>
          </div>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="mt-4 space-y-3">
            {resources.map((resource) => (
              <PermissionSection
                key={resource}
                resource={resource}
                permissions={viewerPermissions[resource]}
                onChange={handleViewerChange}
                disabled={isSaving}
              />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Save Button (Bottom) */}
      {hasChanges() && (
        <div className="flex justify-end space-x-2 pt-4 border-t border-gray-700">
          <Button
            variant="outline"
            onClick={resetPermissions}
            disabled={isSaving}
            className="border-gray-600 hover:bg-gray-800"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Resetar
          </Button>
          <Button
            onClick={savePermissions}
            disabled={isSaving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PermissionsTab;
